import React, { useState } from "react";
import { Box, Typography, Grid, Tooltip, Snackbar, Alert } from "@mui/material";
import { ProductCard, UOM } from "../../features/createSecondaryOrder/CreateSecondaryOrderType";
import DummyProductImage from "../../assets/image/png/dummyProduct.png";
import { OrderType } from "../../types/types";
import { performUomConversionPrimary } from "../../services/catalogueService";
import { updateMultiQtyInCart } from "../../features/createSecondaryOrder/CreateSecondaryOrderService";
import "./CatalogueProductCardPrimary.css";
import { showUom, truncateToTwoDecimals } from "../../components/dms_common/DmsUtils";
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import RenderSvg from "../../components/renderSvg/RenderSvg";
const CatalogueProductCardPrimary: React.FC<ProductCard> = (props) => {
  const {
    productUI,
    updateQuantityInCart,
    orderType,
    handleClickViewSkuDetails,
    catalogue,
    setCart,
    customerInfo,
    hideStockQtyTile,
    ARSQuantities,
    showListView,
    isRestockItem
  } = props;

  const {
    batchCode,
    uom,
    caseToPieceQty,
    otherUnitToPieceQuantity,
    availableCaseQty,
    availableOtherQty,
    availablePieceQty,
  } = productUI || {};

  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const [caseQuantityInput, setCaseQuantityInput] = useState<string>(
    productUI?.caseQuantity?.toString() || ""
  );
  const [pieceQuantityInput, setPieceQuantityInput] = useState<string>(
    productUI?.pieceQuantity?.toString() || ""
  );
  const [otherUnitQuantityInput, setOtherUnitQuantityInput] = useState<string>(
    productUI?.otherUnitQuantity?.toString() || ""
  );

  const [isEditingCaseQuantity, setIsEditingCaseQuantity] = useState<boolean>(false);
  const [isEditingPieceQuantity, setIsEditingPieceQuantity] = useState<boolean>(false);
  const [isEditingOtherUnitQuantity, setIsEditingOtherUnitQuantity] = useState<boolean>(false);

  const ConfigDictionary = JSON.parse(localStorage.getItem("ConfigDictionary") ?? "{}");
  const {
    ptrLabel,
    ptdLabel,
    mrpLabel,
    unitLabel,
    caseUnitLabelPrimary,
    otherUnitLabelPrimary,
    packUnitLabelPrimary,
    currency,
    uomConversionPrimary,
    createPrimaryOrderUom,
  } = ConfigDictionary;

  const handleMultiQtyChange = (newCaseQty: number, newOtherQty: number, newPieceQty: number) => {
    const { convertedCaseQty, convertedOtherQty, convertedPieceQty, hasQtyExceededStock } =
      performUomConversionPrimary(
        newCaseQty,
        newOtherQty,
        newPieceQty,
        uom,
        caseToPieceQty,
        otherUnitToPieceQuantity,
        availableCaseQty,
        availableOtherQty,
        availablePieceQty
      );
    // if (hasQtyExceededStock) setSnackbarOpen(true);
    setCaseQuantityInput(convertedCaseQty.toString());
    setOtherUnitQuantityInput(convertedOtherQty.toString());
    setPieceQuantityInput(convertedPieceQty.toString());
    updateMultiQtyInCart(
      batchCode,
      {
        caseQuantity: convertedCaseQty,
        otherUnitQuantity: convertedOtherQty,
        pieceQuantity: convertedPieceQty,
      },
      customerInfo,
      catalogue,
      setCart
    );
  };

  const isValidARSQuantity = (type: UOM) => {
    if(ARSQuantities) {
      if(type === UOM.CASE && Number(caseQuantityInput) === ARSQuantities.caseQuantity) {
        return false;
      }
      if(type === UOM.PIECE && Number(pieceQuantityInput) === ARSQuantities.pieceQuantity) {
        return false;
      }
      if(type === UOM.OTHER && Number(otherUnitQuantityInput) === ARSQuantities.otherUnitQuantity) {
        return false;
      }
    } 
    return true;
  };

  const handleIncOrDecQty = (type: UOM, change: number) => {
    if (uomConversionPrimary) {
      const newCaseQty = type === UOM.CASE ? +caseQuantityInput + change : +caseQuantityInput;
      const newOtherQty =
        type === UOM.OTHER ? +otherUnitQuantityInput + change : +otherUnitQuantityInput;
      const newPieceQty = type === UOM.PIECE ? +pieceQuantityInput + change : +pieceQuantityInput;
      handleMultiQtyChange(newCaseQty, newOtherQty, newPieceQty);
    } else {
      const newQty =
        type === UOM.CASE
          ? Math.max(+caseQuantityInput + change, 0).toString()
          : type === UOM.OTHER
          ? Math.max(+otherUnitQuantityInput + change, 0).toString()
          : Math.max(+pieceQuantityInput + change, 0).toString();
      type === UOM.CASE
        ? setCaseQuantityInput(newQty)
        : type === UOM.OTHER
        ? setOtherUnitQuantityInput(newQty)
        : setPieceQuantityInput(newQty);
      updateQuantityInCart(batchCode, +newQty, type, customerInfo, catalogue, setCart);
    }
  };

  const handleInputBlur = (type: UOM) => {
    if (uomConversionPrimary) {
      handleMultiQtyChange(+caseQuantityInput, +otherUnitQuantityInput, +pieceQuantityInput);
    } else {
      const newQty =
        type === UOM.CASE
          ? Math.min(+caseQuantityInput, 999999).toString()
          : type === UOM.OTHER
          ? Math.min(+otherUnitQuantityInput,999999).toString()
          : Math.min(+pieceQuantityInput,999999).toString();
      type === UOM.CASE
        ? setCaseQuantityInput(newQty)
        : type === UOM.OTHER
        ? setOtherUnitQuantityInput(newQty)
        : setPieceQuantityInput(newQty);
      updateQuantityInCart(batchCode, +newQty, type, customerInfo, catalogue, setCart);
    }
  };

  const isInStock = Boolean(availableCaseQty || availablePieceQty || availableOtherQty);
  const availableUoms = uom?.replace(/\s+/g, "").split(",");
  const disablePieceUom = !availableUoms.includes("all") && !availableUoms.includes("ea");
  const disableCaseUom = !availableUoms.includes("all") && !availableUoms.includes("cs");
  const disableOtherUom = !availableUoms.includes("all") && !availableUoms.includes("other");

  const getStockCountColour = (inStock: boolean) => {
    if (inStock) {
      return {
        bg: "#F5FFFA",
        text: "#06A85E",
        border: "#06a85e47",
      };
    } else {
      return {
        bg: "#F4F4F4",
        text: "#7C7C7C",
        border: "#7c7c7c40",
      };
    }
  };

  const { bg, text, border } = getStockCountColour(isInStock);

  const checkIfNormalizedQtyMatchesOrExceedsStock = () => {
    const normalizedQty =
      +caseQuantityInput * caseToPieceQty +
      +otherUnitQuantityInput * otherUnitToPieceQuantity +
      +pieceQuantityInput;
    const normalizedStock =
      availableCaseQty * caseToPieceQty +
      availableOtherQty * otherUnitToPieceQuantity +
      availablePieceQty;
    return normalizedQty >= normalizedStock;
  };

  const isValidQuantity = (type: UOM) => {
    if (type === UOM.PIECE) {
      return 999999 > +pieceQuantityInput;
    } else if (type === UOM.CASE) {
      return 999999 > +caseQuantityInput;
    } else {
      return 999999 > +otherUnitQuantityInput;
    }
  };

  const disableAddButton = (type: UOM) => {
    if (
      (type === UOM.CASE && disableCaseUom) ||
      (type === UOM.OTHER && disableOtherUom) ||
      (type === UOM.PIECE && disablePieceUom)
    ) {
      return true;
    }
    // if (uomConversionPrimary) {
    //   return checkIfNormalizedQtyMatchesOrExceedsStock();
    // }
    return (
      (type === UOM.CASE && !availableCaseQty) ||
      (type === UOM.OTHER && !availableOtherQty) ||
      (type === UOM.PIECE && !availablePieceQty)
    );
  };

  const getDisabledButtonStyling = () => {
    return {
      cursor: "not-allowed",
      backgroundColor: "#F4F4F4",
      color: "#7C7C7C",
    };
  };

  const handleSnackbarClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpen(false);
  };

  const unitConfigs = [
    {
      label: caseUnitLabelPrimary,
      show: showUom("caseQty", createPrimaryOrderUom),
    },
    {
      label: otherUnitLabelPrimary,
      show: showUom("otherQty", createPrimaryOrderUom),
    },
    {
      label: packUnitLabelPrimary,
      show: showUom("pieceQty", createPrimaryOrderUom),
    },
  ];

  const visibleUnits = unitConfigs.filter(
    (unit) => unit.label?.trim() && unit.show
  );

  const gridItemSize = visibleUnits.length === 1 ? 12 : visibleUnits.length === 2 ? 6 : 4;

  const showPcMrp = ConfigDictionary?.showPcMrpInCatalogueProductCardPrimary ?? false

  if (showListView) {
    return (
      <div>
        <div className={`CatalogueProductCardListView_container ${isRestockItem ? 'restock-item-card' : ''}`} style={{ position: "relative" }}>
          <Snackbar
            open={snackbarOpen}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
            autoHideDuration={5000}
            onClose={handleSnackbarClose}
          >
            <Alert
              onClose={handleSnackbarClose}
              severity="warning"
              sx={{ width: "100%" }}
            >
              The entered quantity exceeds the available stock. Your quantity
              will be adjusted to match the available stock for this SKU.
            </Alert>
          </Snackbar>
          <div className="CatalogueProductCardListView_container_left"
            style={showPcMrp ? { width: "46%" } : undefined}
            >
            <div className="imagecontainerfhfhf">
              <img
                height={productUI.imageUrl ? "100%" : "92%"}
                width={productUI.imageUrl ? "100%" : "92%"}
                src={
                  productUI.imageUrl ||
                  ConfigDictionary?.defaultCatalogueImage ||
                  DummyProductImage
                }
                alt={productUI.name}
                onError={(e: React.SyntheticEvent<HTMLImageElement, Event>) => {
                  const target = e.target as HTMLImageElement;
                  target.onerror = null; 
                  target.src = DummyProductImage; 
                }}
              />
            </div>
            <div className="skudetailscontainersdadfd">
              <Tooltip title={`${productUI.name}, ${productUI.pieceSize}`} placement="top">
                <p className="skudetailscontainertop">{`${productUI.name}, ${productUI.pieceSize}`}</p>
              </Tooltip>
              <div className="skudetailscontainerbottom">
                <p className="bottomptd">
                  {orderType === OrderType.PRIMARY ? ptdLabel || "PTD" : ptrLabel || "PTR"}* {currency}{" "}
                  { caseUnitLabelPrimary && otherUnitLabelPrimary && packUnitLabelPrimary
                      ? truncateToTwoDecimals(productUI?.packPtr ?? 0)
                      : caseUnitLabelPrimary && otherUnitLabelPrimary
                      ? truncateToTwoDecimals(productUI?.casePtr ?? 0)
                      : caseUnitLabelPrimary && packUnitLabelPrimary
                      ? truncateToTwoDecimals(productUI?.packPtr ?? 0)
                      : truncateToTwoDecimals(productUI?.casePtr ?? 0)
                  }     
                </p>
                <p className="bottommrp">
                  {(() => {
                    let mrpValue;
                    let isCaseMrp = false;

                      if (caseUnitLabelPrimary && otherUnitLabelPrimary && packUnitLabelPrimary) {
                          mrpValue = truncateToTwoDecimals(productUI?.mrp ?? 0);
                      } else if (caseUnitLabelPrimary && otherUnitLabelPrimary) {
                          mrpValue = truncateToTwoDecimals(productUI?.caseMrp ?? 0);
                          isCaseMrp = true;
                      } else if (caseUnitLabelPrimary && packUnitLabelPrimary) {
                          mrpValue = truncateToTwoDecimals(productUI?.mrp ?? 0);
                      } else {
                        mrpValue = truncateToTwoDecimals(productUI?.caseMrp ?? 0);
                        isCaseMrp = true;
                      }

                if (showPcMrp && !isCaseMrp) {
                    return ``; // Hide if it's not case MRP and config says show PC MRP only
                } else if (showPcMrp && isCaseMrp) {
                    return `${mrpLabel || "MRP"} (Cs)* ${currency} ${mrpValue}`;
                } else {
                    return `${mrpLabel || "MRP"}* ${currency} ${mrpValue}`;
                }
                })()}
                </p>
                
                {showPcMrp && (
                  <p className="bottommrp">
                     {`${mrpLabel || "MRP"} (Pc)*`} {currency}{" "}
                      {truncateToTwoDecimals(productUI?.mrp ?? 0)}
                  </p>
                )}
                <div className="sku_details_label_container_listView">
                  <div
                    className="view_sku_details_listView"
                     style={!Boolean(productUI?.schemes?.length)  ? {
                      background: "none",
                      color: "rgb(92 91 91)",
                      paddingLeft: "0px",
                      paddingRight: "0px",
                    } : {}}
                    onClick={() => handleClickViewSkuDetails && handleClickViewSkuDetails(productUI)}
                  >
                    View Details
                  <KeyboardArrowRightIcon
                    style={{
                      color: "rgb(92 91 91)",
                      width: "14px"
                    }}
                  />
                  {Boolean(productUI?.schemes?.length) &&
                    <RenderSvg name="skuDetailsPromoIcon" />
                  }
                  </div>
                  
                </div>
              </div>
            </div>
          </div>
          <div className="CatalogueProductCardListView_container_right">
            <div className="quantityinputscontainerdsfcdsf" style={{ width: `${visibleUnits.length * 12}%` }}>
              <Grid container spacing={0.5} className="product_selection_grid" color={"#f4f4f4"}>
                {caseUnitLabelPrimary?.trim() && showUom("caseQty", createPrimaryOrderUom) && (
                  <Grid item xs={gridItemSize}>
                    <Box className="price_box">
                      <span className="CatalogueProductCard_price_text">
                        {currency}
                        {truncateToTwoDecimals(productUI.casePrice ?? 0)}
                      </span>
                      <span className="CatalogueProductCard_unit_label_text">
                        /{caseUnitLabelPrimary?.toUpperCase()}
                      </span>
                    </Box>
                  </Grid>
                )}
                {otherUnitLabelPrimary?.trim() && showUom("otherQty", createPrimaryOrderUom) && (
                  <Grid item xs={gridItemSize}>
                    <Box className="price_box">
                      <span className="CatalogueProductCard_price_text">
                        {currency}
                        {truncateToTwoDecimals(productUI.otherUnitPrice ?? 0)}
                      </span>
                      <span className="CatalogueProductCard_unit_label_text">
                        /{otherUnitLabelPrimary?.toUpperCase()?.slice(0, 3)}
                      </span>
                    </Box>
                  </Grid>
                )}
                {packUnitLabelPrimary?.trim() && showUom("pieceQty", createPrimaryOrderUom) && (
                  <Grid item xs={gridItemSize}>
                    <Box className="price_box">
                      <span className="CatalogueProductCard_price_text">
                        {currency}
                        {truncateToTwoDecimals(productUI.price ?? 0)}
                      </span>
                      <span className="CatalogueProductCard_unit_label_text">
                        /{packUnitLabelPrimary?.toUpperCase()}
                      </span>
                    </Box>
                  </Grid>
                )}
              </Grid>
              <Grid container spacing={0.5} className="product_selection_grid">
                {caseUnitLabelPrimary && showUom("caseQty", createPrimaryOrderUom) && (
                  <Grid item xs={gridItemSize}>
                    {+caseQuantityInput || isEditingCaseQuantity ? (
                      <Box className="quantity_box">
                        <Box
                          onClick={() => isValidARSQuantity(UOM.CASE) && handleIncOrDecQty(UOM.CASE, -1)}
                          className="increment_decrement_icon decrement_icon_cs"
                          style={isValidARSQuantity(UOM.CASE) ? {} : getDisabledButtonStyling()}
                        >
                          -
                        </Box>
                        <Box sx={{ width: "60%", height: "100%" }}>
                          <input
                            inputMode="numeric"
                            value={caseQuantityInput}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (/^\d{0,6}$/.test(value)) {
                                setCaseQuantityInput(value);
                              }
                            }}
                            onFocus={() => setIsEditingCaseQuantity(true)}
                            onBlur={() => {
                              setIsEditingCaseQuantity(false);
                              handleInputBlur(UOM.CASE);
                            }}
                            className="quantity_input quantity_input_cs"
                          />
                        </Box>
                        <Box
                          onClick={() => isValidQuantity(UOM.CASE) && handleIncOrDecQty(UOM.CASE, 1)}
                          className="increment_decrement_icon increment_icon_cs"
                          style={isValidQuantity(UOM.CASE) ? {} : getDisabledButtonStyling()}
                        >
                          +
                        </Box>
                      </Box>
                    ) : (
                      <Box
                        onClick={() => !disableCaseUom && handleIncOrDecQty(UOM.CASE, 1)}
                        className="add_and_unit_button add_and_unit_button_cs"
                        style={!disableCaseUom ? {} : getDisabledButtonStyling()}
                      >
                        ADD
                        <span style={{ fontSize: "6.5px", fontWeight: 400, color: !disableCaseUom ? "#ffffff" : "#7C7C7C" }}>
                          {caseToPieceQty} {unitLabel?.toUpperCase() || "UNIT"}
                          {caseToPieceQty > 1 ? "S" : ""}
                        </span>
                      </Box>
                    )}
                  </Grid>
                )}
                {otherUnitLabelPrimary && showUom("otherQty", createPrimaryOrderUom) && (
                  <Grid item xs={gridItemSize}>
                    {+otherUnitQuantityInput || isEditingOtherUnitQuantity ? (
                      <Box className="quantity_box">
                        <Box
                          onClick={() => isValidARSQuantity(UOM.OTHER) && handleIncOrDecQty(UOM.OTHER, -1)}
                          className="increment_decrement_icon decrement_icon_other"
                          style={isValidARSQuantity(UOM.OTHER) ? {} : getDisabledButtonStyling()}
                        >
                          -
                        </Box>
                        <Box sx={{ width: "60%", height: "100%" }}>
                          <input
                            inputMode="numeric"
                            value={otherUnitQuantityInput}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (/^\d{0,6}$/.test(value)) {
                                setOtherUnitQuantityInput(value);
                              }
                            }}
                            onFocus={() => setIsEditingOtherUnitQuantity(true)}
                            onBlur={() => {
                              setIsEditingOtherUnitQuantity(false);
                              handleInputBlur(UOM.OTHER);
                            }}
                            className="quantity_input quantity_input_other"
                          />
                        </Box>
                        <Box
                          onClick={() => isValidQuantity(UOM.OTHER) && handleIncOrDecQty(UOM.OTHER, 1)}
                          className="increment_decrement_icon increment_icon_other"
                          style={isValidQuantity(UOM.OTHER) ? {} : getDisabledButtonStyling()}
                        >
                          +
                        </Box>
                      </Box>
                    ) : (
                      <Box
                        onClick={() => !disableOtherUom && handleIncOrDecQty(UOM.OTHER, 1)}
                        className="add_and_unit_button add_and_unit_button_other"
                        style={!disableOtherUom ? {} : getDisabledButtonStyling()}
                      >
                        ADD
                        <span style={{ fontSize: "6.5px", fontWeight: 400, color: !disableOtherUom ? "#ffffff" : "#7C7C7C" }}>
                          {otherUnitToPieceQuantity} {unitLabel?.toUpperCase() || "UNIT"}
                          {otherUnitToPieceQuantity > 1 ? "S" : ""}
                        </span>
                      </Box>
                    )}
                  </Grid>
                )}
                {packUnitLabelPrimary && showUom("pieceQty", createPrimaryOrderUom) && (
                  <Grid item xs={gridItemSize}>
                    {+pieceQuantityInput || isEditingPieceQuantity ? (
                      <Box className="quantity_box">
                        <Box
                          onClick={() => isValidARSQuantity(UOM.PIECE) && handleIncOrDecQty(UOM.PIECE, -1)}
                          className="increment_decrement_icon decrement_icon_pc"
                          style={isValidARSQuantity(UOM.PIECE) ? {} : getDisabledButtonStyling()}
                        >
                          -
                        </Box>
                        <Box sx={{ width: "60%", height: "100%" }}>
                          <input
                            inputMode="numeric"
                            value={pieceQuantityInput}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (/^\d{0,6}$/.test(value)) {
                                setPieceQuantityInput(value);
                              }
                            }}
                            onFocus={() => setIsEditingPieceQuantity(true)}
                            onBlur={() => {
                              setIsEditingPieceQuantity(false);
                              handleInputBlur(UOM.PIECE);
                            }}
                            className="quantity_input quantity_input_pc"
                          />
                        </Box>
                        <Box
                          onClick={() => isValidQuantity(UOM.PIECE) && handleIncOrDecQty(UOM.PIECE, 1)}
                          className="increment_decrement_icon increment_icon_pc"
                          style={isValidQuantity(UOM.PIECE) ? {} : getDisabledButtonStyling()}
                        >
                          +
                        </Box>
                      </Box>
                    ) : (
                      <Box
                        onClick={() => !disablePieceUom && handleIncOrDecQty(UOM.PIECE, 1)}
                        className="add_and_unit_button add_and_unit_button_pc"
                        style={!disablePieceUom ? {} : getDisabledButtonStyling()}
                      >
                        ADD
                        <span style={{ fontSize: "6.5px", fontWeight: 400, color: !disablePieceUom ? "#ffffff" : "#7C7C7C" }}>
                          1 {unitLabel?.toUpperCase() || "UNIT"}
                        </span>
                      </Box>
                    )}
                  </Grid>
                )}
              </Grid>
            </div>
            {!Boolean(hideStockQtyTile) && (
              <div
                className="stock-nos-container"
                style={{ backgroundColor: bg, margin: 0, padding: `4px 6px`, borderRadius: "6px" }}
              >
                <span style={{ color: text, fontSize: "11px" }}>{`${isInStock ? "In" : "Out of"} Stock`}</span>
                <div style={{ gap: "4px" }}>
                  {caseUnitLabelPrimary?.trim() && showUom("caseQty", createPrimaryOrderUom) && (
                    <div className="stock-qty" style={{ borderColor: border, borderRadius: "5px", padding: "2px 6px" }}>
                      <p className="stock-qty-child">
                        <span>{availableCaseQty}</span>
                        <span>{caseUnitLabelPrimary?.toUpperCase()}</span>
                      </p>
                    </div>
                  )}
                  {otherUnitLabelPrimary?.trim() && showUom("otherQty", createPrimaryOrderUom) && (
                    <div className="stock-qty" style={{ borderColor: border, borderRadius: "5px", padding: "2px 6px" }}>
                      <p className="stock-qty-child">
                        <span>{availableOtherQty}</span>
                        <span>{otherUnitLabelPrimary?.toUpperCase()?.slice(0, 3)}</span>
                      </p>
                    </div>
                  )}
                  {packUnitLabelPrimary?.trim() && showUom("pieceQty", createPrimaryOrderUom) && (
                    <div className="stock-qty" style={{ borderColor: border, borderRadius: "5px", padding: "2px 6px" }}>
                      <p className="stock-qty-child">
                        <span>{availablePieceQty}</span>
                        <span>{packUnitLabelPrimary?.toUpperCase()}</span>
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="listViewBottomBorder">
          <p className="listViewBottomBorder_child"></p>
        </div>
      </div>
    );
  }

  return (
    <div className={`CatalogueProductCard ${isRestockItem ? 'restock-item-card' : ''}`}>
      {isRestockItem && <div className="restock-badge">RESTOCK</div>}
      <Snackbar
        open={snackbarOpen}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        autoHideDuration={5000}
        onClose={handleSnackbarClose}
      >
        <Alert onClose={handleSnackbarClose} severity="warning" sx={{ width: "100%" }}>
          The entered quantity exceeds the available stock. Your quantity will be adjusted to match the available stock for this SKU.
        </Alert>
      </Snackbar>
      <div className="CatalogueProductCard_img_container" style={{ backgroundColor: "#fafafa", position: "relative" }}>
        <img
          height={productUI.imageUrl ? 90 : 70}
          src={productUI.imageUrl || ConfigDictionary?.defaultCatalogueImage || DummyProductImage}
          alt={productUI.name}
          onError={(e: React.SyntheticEvent<HTMLImageElement, Event>) => {
            const target = e.target as HTMLImageElement;
            target.onerror = null; // Prevent infinite loop
            target.src = DummyProductImage; // Fallback to dummy image
          }}
        />
      </div>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          padding: "0px 10px 1px 10px",
        }}
      >
        <div>
          <Tooltip title={productUI.name} arrow>
            <Typography
              sx={{
                display: "-webkit-box",
                "-webkit-box-orient": "vertical",
                "-webkit-line-clamp": "2",
                overflow: "hidden",
                fontSize: "12px",
                fontWeight: 600,
                textOverflow: "ellipsis",
                lineClamp: "2",
                lineHeight: "1rem",
                minHeight: "2.6em",
                whiteSpace: "pre-line",
                color: "#5c5c5c",
                marginTop: "8px",
                marginBottom: "6px",
              }}
            >
              {productUI.name}
            </Typography>
          </Tooltip>
          <Typography sx={{ fontSize: "10px", fontWeight: 600 }}>
            {orderType === OrderType.PRIMARY ? ptdLabel || "PTD" : ptrLabel || "PTR"}* {currency}{" "}
            {
              // Check the conditions for showing different prices
              caseUnitLabelPrimary && otherUnitLabelPrimary && packUnitLabelPrimary
  ? truncateToTwoDecimals(productUI?.packPtr ?? 0)
  : caseUnitLabelPrimary && otherUnitLabelPrimary
  ? truncateToTwoDecimals(productUI?.casePtr ?? 0)
  : caseUnitLabelPrimary && packUnitLabelPrimary
  ? truncateToTwoDecimals(productUI?.packPtr ?? 0)
  : truncateToTwoDecimals(productUI?.casePtr ?? 0)
          }
          </Typography>
          <Typography
            sx={{
              fontSize: "10px",
              color: "#7C7C7C",
              marginBottom: "3px",
              lineHeight: "1.4",
            }}
          >

        {(() => {
          let mrpValue;
          let isCaseMrp = false;

          if (caseUnitLabelPrimary && otherUnitLabelPrimary && packUnitLabelPrimary) {
              mrpValue = truncateToTwoDecimals(productUI?.mrp ?? 0);
          } else if (caseUnitLabelPrimary && otherUnitLabelPrimary) {
            mrpValue = truncateToTwoDecimals(productUI?.caseMrp ?? 0);
            isCaseMrp = true;
          } else if (caseUnitLabelPrimary && packUnitLabelPrimary) {
          mrpValue = truncateToTwoDecimals(productUI?.mrp ?? 0);
          }  else {
            mrpValue = truncateToTwoDecimals(productUI?.caseMrp ?? 0);
            isCaseMrp = true;
          }
            if(showPcMrp && !isCaseMrp) {
              return ``; // Hide if it's not case MRP and config says show PC MRP only
            }else if(showPcMrp && isCaseMrp){
              return `${mrpLabel || "MRP"} (Cs)* ${currency} ${mrpValue}`;
            }else {
              return `${mrpLabel || "MRP"}* ${currency} ${mrpValue}`;
            }

        })()}
          
          </Typography>
              
            {showPcMrp && (
                <Typography
                  sx={{
                   fontSize: "10px",
                   color: "#7C7C7C",
                   marginBottom: "3px",
                   lineHeight: "1.4",
                  }}
                  >
                   {`${mrpLabel || "MRP"} (Pc)*`} {currency}{" "}
                  {truncateToTwoDecimals(productUI?.mrp ?? 0)}
                 </Typography> 
            )}

          {!Boolean(hideStockQtyTile)  && 
            <div className="stock-nos-container" style={{ backgroundColor: bg }}>
            <span style={{ color: text }}>{`${isInStock ? "In" : "Out of"} Stock`}</span>
            <div>
                {caseUnitLabelPrimary?.trim() && showUom("caseQty", createPrimaryOrderUom) && (
              <div className="stock-qty" style={{ borderColor: border }}>
                <span>
                  {availableCaseQty} {caseUnitLabelPrimary?.toUpperCase()}
                </span>
              </div>
                )}
              {otherUnitLabelPrimary?.trim() && showUom("otherQty", createPrimaryOrderUom) && (
                <div className="stock-qty" style={{ borderColor: border }}>
                  <span>
                    {availableOtherQty} {otherUnitLabelPrimary?.toUpperCase()?.slice(0, 3)}
                  </span>
                </div>
              )}
              {packUnitLabelPrimary?.trim() && showUom("pieceQty", createPrimaryOrderUom) && (
              <div className="stock-qty" style={{ borderColor: border }}>
                <span>
                  {availablePieceQty} {packUnitLabelPrimary?.toUpperCase()}
                </span>
              </div>
              )}
            </div>
          </div>
          }
        
        </div>
        <div>
          <Grid container spacing={0.5} className="product_selection_grid" color={"#f4f4f4"}>
            {caseUnitLabelPrimary?.trim() && showUom("caseQty", createPrimaryOrderUom) && (
            <Grid item xs={gridItemSize}>
              <Box className="price_box">
                <span className="CatalogueProductCard_price_text">
                  {currency}
                  {truncateToTwoDecimals(productUI.casePrice ?? 0)}
                </span>
                <span className="CatalogueProductCard_unit_label_text">
                  /{caseUnitLabelPrimary?.toUpperCase()}
                </span>
              </Box>
            </Grid>
            )}
            {otherUnitLabelPrimary?.trim() && showUom("otherQty", createPrimaryOrderUom) &&(
              <Grid item xs={gridItemSize}>
                <Box className="price_box">
                  <span className="CatalogueProductCard_price_text">
                    {currency}
                    {truncateToTwoDecimals(productUI.otherUnitPrice ?? 0)}
                  </span>
                  <span className="CatalogueProductCard_unit_label_text">
                    /{otherUnitLabelPrimary?.toUpperCase()?.slice(0, 3)}
                  </span>
                </Box>
              </Grid>
            )}
            {packUnitLabelPrimary?.trim() && showUom("pieceQty", createPrimaryOrderUom) && (
              <Grid item xs={gridItemSize}>
                <Box className="price_box">
                  <span className="CatalogueProductCard_price_text">
                    {currency}
                    {truncateToTwoDecimals(productUI.price ?? 0)}
                  </span>
                  <span className="CatalogueProductCard_unit_label_text">
                    /{packUnitLabelPrimary?.toUpperCase()}
                  </span>
                </Box>
              </Grid>
            )}
          </Grid>
          <Grid container spacing={0.5} className="product_selection_grid">
            {caseUnitLabelPrimary && showUom("caseQty", createPrimaryOrderUom) && (
            <Grid item xs={gridItemSize}>
              {+caseQuantityInput || isEditingCaseQuantity ? (
                <Box className="quantity_box">
                  <Box
                    onClick={() => isValidARSQuantity(UOM.CASE) && handleIncOrDecQty(UOM.CASE, -1)}
                    className="increment_decrement_icon"
                    style={isValidARSQuantity(UOM.CASE) ? {} : getDisabledButtonStyling()}
                  >
                    -
                  </Box>
                  <Box sx={{ width: "40%", height: "100%" }}>
                    <input
                      inputMode="numeric"
                      value={caseQuantityInput}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (/^\d{0,6}$/.test(value)) {
                          setCaseQuantityInput(value);
                        }
                      }}
                      onFocus={() => setIsEditingCaseQuantity(true)}
                      onBlur={() => {
                        setIsEditingCaseQuantity(false);
                        handleInputBlur(UOM.CASE);
                      }}
                      className="quantity_input"
                    />
                  </Box>
                  <Box
                    onClick={() => isValidQuantity(UOM.CASE) && handleIncOrDecQty(UOM.CASE, 1)}
                    className="increment_decrement_icon"
                    style={ isValidQuantity(UOM.CASE) ? {} : getDisabledButtonStyling()}
                  >
                    +
                  </Box>
                </Box>
              ) : (
                <Box
                  onClick={() => !disableCaseUom && handleIncOrDecQty(UOM.CASE, 1)}
                  className="add_and_unit_button"
                  style={!disableCaseUom ? {}: getDisabledButtonStyling() }
                >
                  ADD
                  <span
                    style={{
                      fontSize: "6.5px",
                      fontWeight: 400,
                      color:  !disableCaseUom ? "#ffffff" : "#7C7C7C" ,
                    }}
                  >
                    {caseToPieceQty} {unitLabel?.toUpperCase() || "UNIT"}
                    {caseToPieceQty > 1 ? "S" : ""}
                  </span>
                </Box>
              )}
            </Grid>
            )}
            {otherUnitLabelPrimary && showUom("otherQty", createPrimaryOrderUom) && (
              <Grid item xs={gridItemSize}>
                {+otherUnitQuantityInput || isEditingOtherUnitQuantity ? (
                  <Box className="quantity_box">
                    <Box
                      onClick={() => isValidARSQuantity(UOM.OTHER) && handleIncOrDecQty(UOM.OTHER, -1)}
                      className="increment_decrement_icon"
                      style={isValidARSQuantity(UOM.OTHER) ? {} : getDisabledButtonStyling()}
                    >
                      -
                    </Box>
                    <Box sx={{ width: "40%", height: "100%" }}>
                      <input
                        inputMode="numeric"
                        value={otherUnitQuantityInput}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (/^\d{0,6}$/.test(value)) {
                            setOtherUnitQuantityInput(value);
                          }
                        }}
                        onFocus={() => setIsEditingOtherUnitQuantity(true)}
                        onBlur={() => {
                          setIsEditingOtherUnitQuantity(false);
                          handleInputBlur(UOM.OTHER);
                        }}
                        className="quantity_input"
                      />
                    </Box>
                    <Box
                      onClick={() => isValidQuantity(UOM.OTHER) && handleIncOrDecQty(UOM.OTHER, 1)}
                      className="increment_decrement_icon"
                      style={isValidQuantity(UOM.OTHER) ? {} : getDisabledButtonStyling()}
                    >
                      +
                    </Box>
                  </Box>
                ) : (
                  <Box
                    onClick={() => !disableOtherUom && handleIncOrDecQty(UOM.OTHER, 1)}
                    className="add_and_unit_button"
                    style={!disableOtherUom ? {}: getDisabledButtonStyling()}
                  >
                    ADD
                    <span
                      style={{
                        fontSize: "6.5px",
                        fontWeight: 400,
                        color:  !disableOtherUom ? "#ffffff" : "#7C7C7C"  ,
                      }}
                    >
                      {otherUnitToPieceQuantity} {unitLabel?.toUpperCase() || "UNIT"}
                      {otherUnitToPieceQuantity > 1 ? "S" : ""}
                    </span>
                  </Box>
                )}
              </Grid>
            )}
            {packUnitLabelPrimary && showUom("pieceQty", createPrimaryOrderUom) && (
              <Grid item xs={gridItemSize}>
                {+pieceQuantityInput || isEditingPieceQuantity ? (
                  <Box className="quantity_box">
                    <Box
                      onClick={() => isValidARSQuantity(UOM.PIECE) && handleIncOrDecQty(UOM.PIECE, -1)}
                      className="increment_decrement_icon"
                      style={isValidARSQuantity(UOM.PIECE) ? {} : getDisabledButtonStyling()}
                    >
                      -
                    </Box>
                    <Box sx={{ width: "40%", height: "100%" }}>
                      <input
                        inputMode="numeric"
                        value={pieceQuantityInput}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (/^\d{0,6}$/.test(value)) {
                            setPieceQuantityInput(value);
                          }
                        }}
                        onFocus={() => setIsEditingPieceQuantity(true)}
                        onBlur={() => {
                          setIsEditingPieceQuantity(false);
                          handleInputBlur(UOM.PIECE);
                        }}
                        className="quantity_input"
                      />
                    </Box>
                    <Box
                      onClick={() => isValidQuantity(UOM.PIECE) && handleIncOrDecQty(UOM.PIECE, 1)}
                      className="increment_decrement_icon"
                      style={isValidQuantity(UOM.PIECE) ? {} : getDisabledButtonStyling()}
                    >
                      +
                    </Box>
                  </Box>
                ) : (
                  <Box
                    onClick={() => !disablePieceUom && handleIncOrDecQty(UOM.PIECE, 1)}
                    className="add_and_unit_button"
                    style={!disablePieceUom ? {}: getDisabledButtonStyling()}
                  >
                    ADD
                    <span
                      style={{
                        fontSize: "6.5px",
                        fontWeight: 400,
                        color: !disablePieceUom ? "#ffffff" : "#7C7C7C" ,
                      }}
                    >
                      1 {unitLabel?.toUpperCase() || "UNIT"}
                    </span>
                  </Box>
                )}
              </Grid>
            )}
          </Grid>
        </div>
        <div className="sku_details_label_container">
          <div className="promos_available_primary" style={{ visibility: Boolean(productUI?.schemes?.length) ? `visible` : `hidden` }}>
            <span>Promos Available</span>
          </div>
         <div
            className="view_sku_details"
            onClick={() => handleClickViewSkuDetails && handleClickViewSkuDetails(productUI)}
          >
            View details {'>'}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CatalogueProductCardPrimary;
